<template>
  <q-modal
    :show="show"
    :title="$t(`conversations.filters.calibrations.create_modal.${edit ? 'edit_' : ''}title`)"
    close-button
    narrow
    @close="$emit('close')"
  >
    <form data-testid="calibration-form" :class="$style.form" @submit.prevent="submit">
      <ui-form-field
        :label="$t('conversations.filters.calibrations.create_modal.name_label')"
        :class="$style.nameField"
      >
        <ui-input v-model="name" data-testid="calibration-form-name" name="name" required maxlength="255" />
      </ui-form-field>
      <ui-form-field :label="$t('conversations.filters.calibrations.create_modal.date_label')">
        <div data-testid="calibration-due-at">
          <day-picker v-model="dueAt" :min-date="new Date()" />
        </div>
      </ui-form-field>
    </form>
    <template #actions>
      <div :class="$style.buttons">
        <ui-button secondary @click="$emit('close')">
          {{ $t('universal.cancel') }}
        </ui-button>
        <ui-button data-testid="create-session-btn" type="submit" :disabled="!canSubmit" @click="submit">
          {{ $t(edit ? 'universal.save_changes' : 'conversations.filters.calibrations.create_modal.submit') }}
        </ui-button>
      </div>
    </template>
  </q-modal>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import { computed, ref, watch } from 'vue'
import { UiFormField, UiInput, UiButton } from '@klausapp/ui-kit'
import DayPicker from '@/components/pickers/DayPicker.vue'

import QModal from '@/components/QModal.vue'
import { session } from '@/composables/useSession'
import {
  createCalibrationSession,
  editCalibrationSession,
  type CalibrationSession,
  type EditCalibrationSessionPayload,
} from '@/api/calibration'
import { track } from '@/utils/analytics'

const props = defineProps<{
  active: boolean
  show: boolean
  edit?: CalibrationSession
}>()
const emit = defineEmits<{
  (e: 'close'): void
  (e: 'update'): void
}>()

const getToday = () => dayjs().format()
const name = ref('')
const dueAt = ref(getToday())

const submit = async () => {
  if (!name.value && !dueAt.value) return

  if (props.edit) {
    const calibrationSession: EditCalibrationSessionPayload = {}

    if (name.value !== props.edit?.title) calibrationSession.title = name.value
    if (!dayjs(dueAt.value).isSame(props.edit?.dueAt, 'day') && !dayjs(dueAt.value).isBefore(undefined, 'day'))
      calibrationSession.dueAt = dueAt.value

    await editCalibrationSession(session.workspace.id, props.edit.sessionId, calibrationSession)
    track('Calibration session edited', { type: props.active ? 'active' : 'old' })
  } else {
    await createCalibrationSession(session.workspace.id, { title: name.value, dueAt: dueAt.value })
    track('Calibration session created', { type: props.active ? 'active' : 'old' })
  }

  emit('update')
  emit('close')
}

const canSubmit = computed(() => {
  const canCreateSession = !!name.value.trim() && !!dueAt.value
  const canUpdateSession =
    (!!name.value.trim() && name.value !== props.edit?.title) ||
    (!!dueAt.value &&
      !dayjs(dueAt.value).isSame(props.edit?.dueAt, 'day') &&
      !dayjs(dueAt.value).isBefore(undefined, 'day'))

  return props.edit ? canUpdateSession : canCreateSession
})

watch(
  () => props.show,
  (show: boolean) => {
    if (!show) {
      name.value = ''
      dueAt.value = getToday()
    }
  },
)

watch(
  () => props.edit,
  (edit) => {
    if (edit) {
      name.value = edit.title
      dueAt.value = edit.dueAt
    }
  },
)
</script>

<style lang="scss" module>
.nameField {
  margin-bottom: 24px;
}

.dateFieldContent {
  display: flex;
  position: relative;

  svg {
    position: absolute;
    top: 12px;
    left: 14px;
    stroke: var(--gray-50);
  }

  .dateInput {
    padding-left: 36px;
  }
}

.form {
  padding-top: 16px;
}

.buttons {
  display: flex;
  justify-content: flex-end;
  padding: 56px 0 4px;
  gap: 8px;
}
</style>
